import {omit, pick} from 'lodash-es';
import UrlUtils from '@spothero/utils/url';
import {
    authAPIService,
    cpAPIService,
    mockOperatorAPIService,
    monolithAPIService,
    monolithAPIServiceV2,
} from 'api/services';
import {ApiResponse} from './api.types';
import {AxiosRequestConfig} from 'axios';

/** @module api/wrappers */

/**
 * Returns the facilitiy ids from a collection of facilities
 * @param facilities - The facilities to get the ids from
 * @returns Comma separated list of facility ids
 */
export function getFacilityIds(facilities: {id: string}[]): string {
    return facilities.map((facility) => facility.id).toString();
}

/**
 * GET utility function for spothero API service
 * @param path - path to endpoint
 * @param params - data to send to endpoint
 * @param config - config to send to endpoint (optional)
 */
export const spotheroGet = <T, P = Record<string, unknown>>(
    path: string,
    params?: P,
    config: AxiosRequestConfig = {}
): Promise<ApiResponse<T>> => {
    return monolithAPIService.get(path, {params, ...config});
};

/**
 * POST utility function for spothero API service
 * @param path - path to endpoint
 * @param data - data to send to endpoint
 * @param config - config to send to endpoint (optional)
 * @param stringify - Whether or not to encode the data as a query string
 */
export const spotheroPost = <T, P = Record<string, unknown>>(
    path: string,
    data: P,
    config?: AxiosRequestConfig,
    stringify = true
): Promise<ApiResponse<T>> => {
    return monolithAPIService.post(
        path,
        stringify ? UrlUtils.createQueryString(data) : data,
        config
    );
};

/**
 * PATCH utility function for spothero API service
 * @param path - path to endpoint
 * @param data - data to send to endpoint
 * @param config - config to send to endpoint (optional)
 */
export const spotheroPatch = <T, P = Record<string, unknown>>(
    path: string,
    data: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return monolithAPIService.patch(path, data, config);
};

/**
 * PUT utility function for spothero API service
 * @param path - path to endpoint
 * @param data - data to send to endpoint
 * @param config - config to send to endpoint (optional)
 */
export const spotheroPut = <T, P = Record<string, unknown>>(
    path: string,
    data?: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return monolithAPIService.put(path, data, config);
};

/**
 * DELETE utility function for spothero API service
 * @param path - Path to endpoint
 * @param config - Optional Axios config (keeps existing behavior)
 * @param data - Optional data to be converted into query params and appended to the path
 */
export const spotheroDelete = <T, P = Record<string, unknown>>(
    path: string,
    config?: AxiosRequestConfig,
    data?: P
): Promise<ApiResponse<T>> => {
    const queryString = data ? `?${UrlUtils.createQueryString(data)}` : '';

    return monolithAPIService.delete(`${path}${queryString}`, config);
};

/**
 * GET utility function for monolith API service v2
 * @param path - Path to endpoint
 * @param params - Data to send to endpoint as params
 * @param config - Config to send to endpoint (optional)
 */
export const spotheroV2Get = <T, P = Record<string, unknown>>(
    path: string,
    params?: P,
    config: AxiosRequestConfig = {}
): Promise<ApiResponse<T>> => {
    return monolithAPIServiceV2.get(path, {params, ...config});
};

/**
 * POST utility function for monolith API service v2
 * @param path - Path to endpoint
 * @param data - Data to send to endpoint
 * @param config - Config to send to endpoint (optional)
 * @param stringify - Whether or not to encode the data as a query string
 */
export const spotheroV2Post = <T, P = Record<string, unknown>>(
    path: string,
    data: P,
    config?: AxiosRequestConfig,
    stringify = true
): Promise<ApiResponse<T>> => {
    return monolithAPIServiceV2.post(
        path,
        stringify ? UrlUtils.createQueryString(data) : data,
        config
    );
};

/**
 * PATCH utility function for monolith API service v2
 * @param path - Path to endpoint
 * @param data - Data to send to endpoint
 * @param config - Config to send to endpoint (optional)
 */
export const spotheroV2Patch = <T, P = Record<string, unknown>>(
    path: string,
    data: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return monolithAPIServiceV2.patch(path, data, config);
};

/**
 * PUT utility function for monolith API service v2
 * @param path - Path to endpoint
 * @param data - Data to send to endpoint
 * @param config - Config to send to endpoint (optional)
 */
export const spotheroV2Put = <T, P = Record<string, unknown>>(
    path: string,
    data?: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return monolithAPIServiceV2.put(path, data, config);
};

/**
 * DELETE utility function for monolith API service v2
 * @param path - Path to endpoint
 * @param config - Config to send to endpoint (optional)
 */
export const spotheroV2Delete = <T>(
    path: string,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return monolithAPIServiceV2.delete(path, config);
};

/**
 * POST wrapper function for control panel service reports
 * @param path - path to endpoint
 * @param data - data to send to endpoint
 * @param config - config to send to endpoint (optional)
 */
export const reportingPost = <T, P extends object = Record<string, unknown>>(
    path: string,
    data: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return cpAPIService.post(
        path,
        UrlUtils.createQueryString(pick(data, 'facilityIDs')),
        {
            params: omit(data, 'facilityIDs'),
            ...config,
        }
    );
};

/**
 * GET wrapper for the control panel service
 * @param path - path to endpoint
 * @param params - data to send to endpoint
 * @param config - config to send to endpoint (optional)
 */
export const cpServiceGet = <T, P = Record<string, unknown>>(
    path: string,
    params?: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return cpAPIService.get(path, {params, ...config});
};

/**
 * POST wrapper for the control panel service
 * @param path - path to endpoint
 * @param data - data to send to endpoint
 * @param config - config to send to endpoint (optional)
 */
export const cpServicePost = <T, P = Record<string, unknown>>(
    path: string,
    data?: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return cpAPIService.post(path, data, config);
};

/**
 * PATCH wrapper for the control panel service
 * @param path - path to endpoint
 * @param data - data to send to endpoint
 * @param config - config to send to endpoint (optional)
 */
export const cpServicePatch = <T, P = Record<string, unknown>>(
    path: string,
    data: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return cpAPIService.patch(path, data, config);
};

/**
 * PUT wrapper for the control panel service
 * @param path - path to endpoint
 * @param data - data to send to endpoint
 * @param config - config to send to endpoint (optional)
 */
export const cpServicePut = <T, P = Record<string, unknown>>(
    path: string,
    data: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return cpAPIService.put(path, data, config);
};

/**
 * POST wrapper for the monolith auth service
 * @param path - path to endpoint
 * @param data - data to send to endpoint
 * @param config - config to send to endpoint (optional)
 */
export const authServicePost = <T, P = Record<string, unknown>>(
    path: string,
    data: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return authAPIService.post(path, data, config);
};

/**
 * Mock Operator API Service Get
 * This function is used to make a GET request to a mock Operator API service.
 * @param path - The API path to make the request to
 * @param data - The data to be sent with the request
 * @param config - Additional configuration for the request
 */
export const mockOperatorAPIServiceGet = <T, P = Record<string, unknown>>(
    path: string,
    data?: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return mockOperatorAPIService.get(path, {params: data, ...config});
};

/**
 * A function that mocks the post method of the Operator API service
 * @param path - The endpoint path
 * @param data - The data to be sent in the request body
 * @param config - Optional configuration for the request
 */
export const mockOperatorAPIServicePost = <T, P = Record<string, unknown>>(
    path: string,
    data: P,
    config?: AxiosRequestConfig
): Promise<ApiResponse<T>> => {
    return mockOperatorAPIService.post(path, data, config);
};
